import { useCallback, useEffect } from 'react'
import { useSelector } from '@/libs/state'
import { GLOBAL_SHEET_IDS, useSheet } from '@/features/ui/hooks/use-sheet'
import { customers, customerEnrichment, notify } from '../wiring'
import { cancelCustomerEnrichment } from '../application/cancel-customer-enrichment'
import { clearCustomerEnrichment } from '../application/clear-customer-enrichment'
import { deleteCustomer } from '../application/delete-customer'
import { requestCustomerEnrichment } from '../application/request-customer-enrichment'
import { upsertCustomer } from '../application/upsert-customer'
import { customerEdit$, refreshCustomerList } from '../store'
import type { SubmitValues } from '../ui/customer-form'

const useCustomerEditSheet = () => {
	const { isOpen, entityId: customerId, close } = useSheet(GLOBAL_SHEET_IDS.CUSTOMER_EDIT)
	const customer = useSelector(customerEdit$)

	const loadCustomer = useCallback(async (): Promise<void> => {
		if (!customerId) {
			customerEdit$.set(null)
			return
		}
		const next = await customers.getById(customerId)
		customerEdit$.set(next)
	}, [customerId])

	useEffect(() => {
		loadCustomer()
	}, [loadCustomer])

	const reloadCustomer = async (): Promise<void> => {
		await loadCustomer()
		refreshCustomerList()
	}

	const handleSubmit = async (values: SubmitValues): Promise<void> => {
		await upsertCustomer(
			customers,
			{
				id: customerId!,
				kind: values.kind,
				name: values.name,
				email: values.email,
				taxId: values.taxId ?? null,
				phone: values.phone ?? null,
				website: values.website ?? null,
				contact: values.contact ?? null,
				logo: values.logo,
				addressLine1: values.addressLine1 ?? null,
				addressLine2: values.addressLine2 ?? null,
				city: values.city ?? null,
				state: values.state ?? null,
				country: values.country ?? null,
				countryCode: values.countryCode ?? null,
				zip: values.zip ?? null,
				note: values.note ?? null,
			},
			notify,
		)
		refreshCustomerList()
		close()
	}

	const handleDelete = async (): Promise<void> => {
		if (!customerId) return
		await deleteCustomer(customers, customerId, notify)
		refreshCustomerList()
		close()
	}

	const handleStartEnrichment = async (): Promise<void> => {
		if (!customerId) return
		try {
			await requestCustomerEnrichment(customerEnrichment, customerId)
			notify.success('customer.enrichRequested')
			await reloadCustomer()
		} catch {
			notify.error('customer.enrichRequestFailed')
		}
	}

	const handleCancelEnrichment = async (): Promise<void> => {
		if (!customerId) return
		try {
			await cancelCustomerEnrichment(customerEnrichment, customerId)
			notify.success('customer.enrichCancelled')
			await reloadCustomer()
		} catch {
			notify.error('customer.enrichCancelFailed')
		}
	}

	const handleClearEnrichment = async (): Promise<void> => {
		if (!customerId) return
		try {
			await clearCustomerEnrichment(customerEnrichment, customerId)
			notify.success('customer.enrichCleared')
			await reloadCustomer()
		} catch {
			notify.error('customer.enrichClearFailed')
		}
	}

	const isEnriching = customer?.enrichmentStatus === 'pending' || customer?.enrichmentStatus === 'processing'
	const hasEnrichmentData = Boolean(customer?.description || customer?.industry || customer?.companyType)
	const canEnrich = Boolean(customer?.website || customer?.email)

	return {
		customer,
		isOpen,
		close,
		isEnriching,
		hasEnrichmentData,
		canEnrich,
		handleSubmit,
		handleDelete,
		handleStartEnrichment,
		handleCancelEnrichment,
		handleClearEnrichment,
	}
}

export { useCustomerEditSheet }
